// src/pages/lab/protocols/components/stepEditingComponents/StepMaterialsEditor.tsx
import React, { useState } from "react";
import { Table, Button, Drawer, Input, InputNumber, Space, Modal, Tooltip, message, Badge, Select } from "antd";
import { PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";

import { ProtocolStepMaterial } from "../../../../../../types/lab/ProtocolTypes";

const { TextArea } = Input;
const { confirm } = Modal;

const unitOptions = [
	{ label: "µL (microliters)", value: "µL" },
	{ label: "mL (milliliters)", value: "mL" },
	{ label: "L (liters)", value: "L" },
	{ label: "mg (milligrams)", value: "mg" },
	{ label: "g (grams)", value: "g" },
	{ label: "kg (kilograms)", value: "kg" },
	{ label: "units", value: "units" },
	{ label: "wells", value: "wells" },
	{ label: "reactions", value: "reactions" },
	{ label: "pieces", value: "pieces" },
	{ label: "per sample", value: "per sample" },
];

interface StepMaterialsEditorProps {
	materials: ProtocolStepMaterial[];
	onMaterialsChange: (materials: ProtocolStepMaterial[]) => void;
}

const StepMaterialsEditor: React.FC<StepMaterialsEditorProps> = ({ materials, onMaterialsChange }) => {
	const [editingMaterial, setEditingMaterial] = useState<Partial<ProtocolStepMaterial & { unit?: string }>>({
		name: "",
		description: "",
		quantity: 1,
		unit: "µL",
	});
	const [materialDrawerVisible, setMaterialDrawerVisible] = useState(false);

	const handleAddMaterial = () => {
		setEditingMaterial({ name: "", description: "", quantity: 1, unit: "µL" });
		setMaterialDrawerVisible(true);
	};

	const handleEditMaterial = (material: ProtocolStepMaterial) => {
		const unitMatch = material.description?.match(/\((\d+(?:\.\d+)?)\s*([^)]+)\)/);
		const unit = unitMatch ? unitMatch[2] : "µL";
		setEditingMaterial({ ...material, unit });
		setMaterialDrawerVisible(true);
	};

	const handleDuplicateMaterial = (material: ProtocolStepMaterial) => {
		const unitMatch = material.description?.match(/\((\d+(?:\.\d+)?)\s*([^)]+)\)/);
		const unit = unitMatch ? unitMatch[2] : "µL";
		setEditingMaterial({
			name: `${material.name} (Copy)`,
			description: material.description,
			quantity: material.quantity,
			unit,
		});
		setMaterialDrawerVisible(true);
	};

	const handleDeleteMaterial = (material: ProtocolStepMaterial) => {
		confirm({
			title: "Confirm Deletion",
			content: `Are you sure you want to delete material "${material.name}"?`,
			onOk: () => {
				onMaterialsChange(materials.filter((m) => m.name !== material.name));
			},
		});
	};

	const handleSaveMaterial = () => {
		if (!editingMaterial.name?.trim() || !editingMaterial.quantity) {
			message.error("Material name and quantity are required");
			return;
		}

		const updatedMaterials = [...materials];
		const existingIndex = updatedMaterials.findIndex((m) => m.name === editingMaterial.name);

		const now = new Date().toISOString();
		const baseDescription = editingMaterial.description || "";
		const quantityUnit = `(${editingMaterial.quantity} ${editingMaterial.unit})`;
		const description = baseDescription ? `${baseDescription} ${quantityUnit}` : quantityUnit;

		const materialToSave: ProtocolStepMaterial = {
			name: editingMaterial.name!,
			description,
			quantity: editingMaterial.quantity!,
			created_at: editingMaterial.created_at || now,
			updated_at: now,
		};

		if (existingIndex >= 0) {
			updatedMaterials[existingIndex] = materialToSave;
		} else {
			updatedMaterials.push(materialToSave);
		}

		onMaterialsChange(updatedMaterials);
		setMaterialDrawerVisible(false);
		setEditingMaterial({ name: "", description: "", quantity: 1, unit: "µL" });
	};

	const columns = [
		{
			title: "Material Information",
			key: "info",
			render: (record: ProtocolStepMaterial) => (
				<div>
					<div style={{ fontWeight: 500, fontSize: "12px", marginBottom: 4 }}>{record.name}</div>
					{record.description && <div style={{ fontSize: "11px", color: "var(--text-color-secondary)", maxWidth: 250 }}>{record.description}</div>}
				</div>
			),
		},
		{
			title: "Quantity",
			key: "quantity",
			width: 120,
			render: (record: ProtocolStepMaterial) => {
				const unitMatch = record.description?.match(/\((\d+(?:\.\d+)?)\s*([^)]+)\)/);
				const displayQuantity = unitMatch ? `${unitMatch[1]} ${unitMatch[2]}` : `${record.quantity} units`;
				return <Badge count={displayQuantity} style={{ backgroundColor: "#1890ff", fontSize: "10px" }} />;
			},
		},
		{
			title: "Actions",
			key: "actions",
			width: 120,
			render: (record: ProtocolStepMaterial) => (
				<Space size={4}>
					<Tooltip title="Edit">
						<Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditMaterial(record)} />
					</Tooltip>
					<Tooltip title="Duplicate">
						<Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleDuplicateMaterial(record)} />
					</Tooltip>
					<Tooltip title="Delete">
						<Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteMaterial(record)} />
					</Tooltip>
				</Space>
			),
		},
	];

	return (
		<div>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
				<div>
					<h6 style={{ fontSize: "12px", fontWeight: 500, margin: "0 0 4px 0" }}>Required Materials</h6>
					<p style={{ fontSize: "11px", color: "var(--text-color-secondary)", margin: 0 }}>Specify all reagents, consumables, and materials needed</p>
				</div>
				<Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddMaterial}>
					Add Material
				</Button>
			</div>

			{materials.length > 0 ? (
				<Table columns={columns} dataSource={materials} rowKey="name" pagination={false} size="small" />
			) : (
				<div style={{ textAlign: "center", padding: "48px 24px", border: "2px dashed var(--border-color)", borderRadius: 8 }}>
					<div style={{ fontSize: "48px", marginBottom: 16 }}>📦</div>
					<h6 style={{ fontSize: "13px", margin: "0 0 4px 0" }}>No Materials Defined</h6>
					<p style={{ fontSize: "11px", color: "var(--text-color-secondary)", maxWidth: 320, margin: "0 auto 16px" }}>Add the reagents, consumables, and materials needed</p>
					<Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddMaterial}>
						Add First Material
					</Button>
				</div>
			)}

			<Drawer
				title={editingMaterial.name ? "Edit Material" : "Add New Material"}
				open={materialDrawerVisible}
				onClose={() => setMaterialDrawerVisible(false)}
				width={500}
				footer={
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<div style={{ fontSize: "11px", color: "var(--text-color-secondary)" }}>
							{editingMaterial.quantity} {editingMaterial.unit} • {editingMaterial.name || "New Material"}
						</div>
						<Space size="small">
							<Button icon={<CloseOutlined />} onClick={() => setMaterialDrawerVisible(false)} size="small">
								Cancel
							</Button>
							<Button type="primary" icon={<SaveOutlined />} onClick={handleSaveMaterial} disabled={!editingMaterial.name?.trim() || !editingMaterial.quantity} size="small">
								Save Material
							</Button>
						</Space>
					</div>
				}
			>
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					<div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Material Name *</label>
							<Input value={editingMaterial.name || ""} onChange={(e) => setEditingMaterial({ ...editingMaterial, name: e.target.value })} placeholder="Or enter custom material name" size="small" />
						</div>

						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Quantity *</label>
							<Space.Compact style={{ width: "100%" }}>
								<InputNumber value={editingMaterial.quantity || 1} onChange={(value) => setEditingMaterial({ ...editingMaterial, quantity: value || 1 })} min={0.1} step={0.1} size="small" style={{ flex: 1 }} />
								<Select value={editingMaterial.unit || "µL"} options={unitOptions} onChange={(value) => setEditingMaterial({ ...editingMaterial, unit: value })} size="small" style={{ width: 100 }} />
							</Space.Compact>
						</div>
					</div>

					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Additional Notes</label>
						<TextArea
							value={editingMaterial.description || ""}
							onChange={(e) => setEditingMaterial({ ...editingMaterial, description: e.target.value })}
							placeholder="Storage conditions, preparation notes, supplier information..."
							rows={3}
							size="small"
						/>
						<div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginTop: 4 }}>Include any special handling, storage, or preparation requirements</div>
					</div>
				</Space>
			</Drawer>
		</div>
	);
};

export default StepMaterialsEditor;
